/**
 * 統一通知システムのUIコンポーネント
 */

import React, { useEffect } from 'react';
import { useNotificationStore, Notification, NotificationAction, setupErrorHandlerIntegration } from '../stores/notificationStore';
import { NotificationType } from '../stores/notificationStore';
import * as styles from './NotificationSystem.css.ts';

/**
 * 通知アイテムのプロパティ
 */
interface NotificationItemProps {
  notification: Notification;
  onDismiss: (id: string) => void;
}

/**
 * 個別の通知アイテムコンポーネント
 */
const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onDismiss }) => {
  const getIcon = (type: NotificationType) => {
    switch (type) {
      case 'info':
        return 'ℹ️';
      case 'success':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      case 'critical':
        return '🚨';
      default:
        return 'ℹ️';
    }
  };

  const getTypeClass = (type: NotificationType) => {
    switch (type) {
      case 'info':
        return styles.info;
      case 'success':
        return styles.success;
      case 'warning':
        return styles.warning;
      case 'error':
        return styles.error;
      case 'critical':
        return styles.critical;
      default:
        return styles.info;
    }
  };

  return (
    <div className={`${styles.notification} ${getTypeClass(notification.type)}`}>
      <div className={styles.header}>
        <span className={styles.icon}>{getIcon(notification.type)}</span>
        {notification.title && (
          <span className={styles.title}>{notification.title}</span>
        )}
        {notification.dismissible && (
          <button
            className={styles.closeButton}
            onClick={() => onDismiss(notification.id)}
            aria-label="Dismiss notification"
          >
            ✕
          </button>
        )}
      </div>
      
      <div className={styles.body}>
        <div className={styles.message}>{notification.message}</div>
        
        {notification.details && (
          <details className={styles.details}>
            <summary>Details</summary>
            <pre>{notification.details}</pre>
          </details>
        )}
        
        {notification.actions && notification.actions.length > 0 && (
          <div className={styles.actions}>
            {notification.actions.map((action: NotificationAction, index: number) => (
              <button
                key={index}
                className={`${styles.actionButton} ${styles[action.style || 'secondary']}`}
                onClick={action.action}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {notification.duration && (
        <div 
          className={styles.progressBar}
          style={{ 
            animationDuration: `${notification.duration}ms` 
          }}
        />
      )}
    </div>
  );
};

/**
 * 通知システムのメインコンポーネント
 */
export const NotificationSystem: React.FC = () => {
  const notifications = useNotificationStore(state => state.notifications);
  const dismiss = useNotificationStore(state => state.dismiss);
  const dismissAll = useNotificationStore(state => state.dismissAll);

  // キーボードショートカットの設定
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Escape キーですべての通知を閉じる
      if (e.key === 'Escape' && notifications.length > 0) {
        dismissAll();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [notifications.length, dismissAll]);

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div
      className={styles.container}
    >
      <div className={styles.notificationList}>
        {notifications.map(notification => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onDismiss={dismiss}
          />
        ))}
      </div>

      {notifications.length > 1 && (
        <button
          className={styles.dismissAllButton}
          onClick={dismissAll}
        >
          Dismiss All ({notifications.length})
        </button>
      )}
    </div>
  );
};

/**
 * 通知システムの初期化フック
 * アプリケーションのルートで一度だけ呼び出す
 */
export function useNotificationSystemInit() {
  useEffect(() => {
    const setup = async () => {
      // エラーハンドラーとの連携を設定
      await setupErrorHandlerIntegration();

      // グローバルエラーハンドラーの設定
      const { setupGlobalErrorHandlers } = await import('../utils/error-handler');
      setupGlobalErrorHandlers();
    };
    
    setup();
  }, []);
}